// src/components/HeroShadeDial.jsx
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import gsap from "gsap";

/* Language-neutral on purpose — it reads the same in all three languages,
   so the ring never needs translating and never changes width. */
const RING_TEXT =
  "LABINA24\u00AE  \u00B7  MADE IN GERMANY  \u00B7  PERMANENT MAKE-UP  \u00B7  SINCE 2004  \u00B7  ";

const CYCLE_MS = 3200;

export default function HeroShadeDial({ shades = [] }) {
  const root = useRef(null);
  const navigate = useNavigate();

  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const active = shades[index % (shades.length || 1)];

  /* Slow rotation on the ring. The disc itself never rotates — spinning a
     solid colour looks broken, and the ring alone reads as motion. */
  useLayoutEffect(() => {
    const el = root.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = gsap.context(() => {
      gsap.to(".dial-ring", {
        rotate: 360,
        duration: 34,
        ease: "none",
        repeat: -1,
        transformOrigin: "50% 50%",
      });

      gsap.from(".dial-scale", {
        scale: 0.72,
        opacity: 0,
        duration: 1.2,
        ease: "expo.out",
        delay: 0.35,
      });
    }, root);

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    if (paused || shades.length < 2) return;
    const id = setInterval(
      () => setIndex((i) => (i + 1) % shades.length),
      CYCLE_MS,
    );
    return () => clearInterval(id);
  }, [paused, shades.length]);

  if (!active) return null;

  const clickable = Boolean(active.id);

  return (
    <Wrap
      ref={root}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-hidden="true"
    >
      <Scale className="dial-scale">
        <Ring className="dial-ring" viewBox="0 0 300 300">
          <defs>
            <path
              id="dialRingPath"
              d="M150,150 m-118,0 a118,118 0 1,1 236,0 a118,118 0 1,1 -236,0"
              fill="none"
            />
          </defs>
          <text>
            {/* textLength = the circle's circumference (2π × 118), so the
                text always closes the ring exactly, whatever font loads */}
            <textPath
              href="#dialRingPath"
              startOffset="0"
              textLength="738"
              lengthAdjust="spacing"
            >
              {RING_TEXT.repeat(2)}
            </textPath>
          </text>
        </Ring>

        <Disc
          as={clickable ? "button" : "div"}
          $hex={active.hex}
          $clickable={clickable}
          onClick={
            clickable ? () => navigate(`/produktet/${active.id}`) : undefined
          }
          tabIndex={-1}
        >
          <Inner>
            <Name key={active.nameSq}>{active.nameSq}</Name>
            {active.code && <Code>{active.code}</Code>}
          </Inner>
        </Disc>
      </Scale>
    </Wrap>
  );
}

/* ------------------------------------------------------------------ */

const Wrap = styled.div`
  position: absolute;
  top: 50%;
  right: clamp(24px, 7vw, 120px);
  transform: translateY(-58%);
  z-index: 1;
  pointer-events: auto;

  /* The hero headline is the focus below this width — the dial would
     crowd it rather than balance it. */
  @media (max-width: 1100px) {
    display: none;
  }
`;

const Scale = styled.div`
  position: relative;
  width: clamp(300px, 26vw, 400px);
  aspect-ratio: 1 / 1;
  display: grid;
  place-items: center;
`;

const Ring = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;

  text {
    font-family: ${({ theme }) => theme.font.sans};
    font-size: 11px;
    font-weight: ${({ theme }) => theme.weight.medium};
    letter-spacing: 3.4px;
    fill: ${({ theme }) => theme.color.muted};
    text-transform: uppercase;
  }
`;

const Disc = styled.div`
  position: relative;
  width: 68%;
  aspect-ratio: 1 / 1;
  border-radius: 50%;
  border: none;
  padding: 0;
  display: grid;
  place-items: center;
  background: ${({ $hex }) => $hex || "#ddd"};
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
  /* the colour is the content, so it cross-fades rather than cuts */
  transition:
    background 0.9s ${({ theme }) => theme.ease.out},
    transform 0.5s ${({ theme }) => theme.ease.out};

  &:hover {
    transform: scale(1.04);
  }
`;

const Inner = styled.span`
  display: block;
  padding: 0 18px;
  text-align: center;
  /* Pigments run from Weiss to Tiefschwarz, so a fixed ink colour would
     vanish at one end. A blurred plate keeps the label legible on both. */
  background: rgba(255, 255, 255, 0.86);
  backdrop-filter: blur(10px) saturate(160%);
  -webkit-backdrop-filter: blur(10px) saturate(160%);
  border-radius: ${({ theme }) => theme.radius.pill};
  padding: 10px 20px;
`;

const Name = styled.span`
  display: block;
  font-family: ${({ theme }) => theme.font.display};
  font-size: 0.95rem;
  font-weight: ${({ theme }) => theme.weight.medium};
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.color.ink};
  white-space: nowrap;

  @media (prefers-reduced-motion: no-preference) {
    animation: dialSwap 0.5s ${({ theme }) => theme.ease.out};
  }

  @keyframes dialSwap {
    from {
      opacity: 0;
      transform: translateY(6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Code = styled.span`
  display: block;
  margin-top: 2px;
  font-size: 0.62rem;
  letter-spacing: 0.1em;
  color: ${({ theme }) => theme.color.muted};
  white-space: nowrap;
`;
